import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import axios, { AxiosError, AxiosResponse } from "axios";
import { PINCODE_WEATHER_APIS } from "@/api_framework/api_config";
import { useQuickCommerce } from "@/app/hooks/useQuickCommerce";
import { IPincodeWeather, IPincodeWeatherApiDoc, IRainSettings } from "./interface";
import { mapApiDocToPincodeWeather, toUpdateBody } from "./helpers";

interface IWeatherListResponse {
  status: boolean;
  message: string;
  data: IPincodeWeatherApiDoc[];
}

export const usePincodeWeatherAPI = () => {
  const quickCommerce = useQuickCommerce();
  const [pincodeWeatherList, setPincodeWeatherList] = useState<IPincodeWeather[]>([]);
  const [isListLoading, setIsListLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const authHeaders = useCallback(() => {
    const accessToken = localStorage.getItem(quickCommerce.localStorage.ACCESS_TOKEN.key);
    return { Authorization: `${accessToken}`, "Content-Type": "application/json" };
  }, [quickCommerce]);

  const fetchList = useCallback(async (params?: { search?: string }) => {
    setIsListLoading(true);
    try {
      const search = params?.search?.trim();
      const res: AxiosResponse<IWeatherListResponse> = await axios.get(PINCODE_WEATHER_APIS.PINCODE_WEATHER, {
        headers: authHeaders(),
        params: search ? { search } : undefined,
      });
      if (res.data?.status) {
        setPincodeWeatherList((res.data.data ?? []).map(mapApiDocToPincodeWeather));
      } else {
        toast.error(res.data?.message || "Failed to load pincode weather settings.");
      }
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? (error as AxiosError<{ message?: string }>).response?.data?.message
        : undefined;
      toast.error(message || "Failed to load pincode weather settings. Please try again.");
    } finally {
      setIsListLoading(false);
    }
  }, [authHeaders]);

  const saveRainSettings = useCallback(
    async (pincodes: string[], settings: IRainSettings): Promise<boolean> => {
      if (pincodes.length === 0) return false;
      setIsSaving(true);
      const body = toUpdateBody(settings);
      try {
        const isBulk = pincodes.length > 1;
        const request = isBulk
          ? axios.patch(
              PINCODE_WEATHER_APIS.PINCODE_WEATHER_BULK,
              JSON.stringify({ pincodes, ...body }),
              { headers: authHeaders() },
            )
          : axios.put(
              `${PINCODE_WEATHER_APIS.PINCODE_WEATHER}${pincodes[0]}/`,
              JSON.stringify(body),
              { headers: authHeaders() },
            );

        const res: AxiosResponse<{ status: boolean; message: string }> = await request;
        if (!res.data?.status) {
          toast.error(res.data?.message || "Failed to update rain settings.");
          return false;
        }

        const updated = new Set(pincodes);
        setPincodeWeatherList((prev) =>
          prev.map((item) =>
            updated.has(item.pincode)
              ? {
                  ...item,
                  isRainy: settings.isRainy,
                  isEventActive: !!settings.isEventActive,
                  startTime: settings.startTime,
                  endTime: settings.endTime,
                  reason:settings.reason,
                }
              : item,
          ),
        );
        toast.success(isBulk ? `Rain settings updated for ${pincodes.length} pincodes.` : "Rain settings updated.");
        return true;
      } catch (error) {
        const message = axios.isAxiosError(error)
          ? (error as AxiosError<{ message?: string }>).response?.data?.message
          : undefined;
        toast.error(message || "Failed to update rain settings. Please try again.");
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [authHeaders],
  );

  return { pincodeWeatherList, isListLoading, isSaving, fetchList, saveRainSettings };
};

